import {handleErrors, axiosErrors, API1, apiGet, apiDelete, apiPost, apiPut} from './fetchHelpers';
import * as act from './actionCreators';
import axios from 'axios';

// `api${httpAction}` functions are called with
//   `request`: [optional] initial request redux action
//   `success`: success redux action, which is dispatched with payload from the api response
//   `url`
//   `payload`: [optional] data to send in post/put requests
// what, no error response? correct, these all handle api errors uniformly by dispatching a toast notification
// please keep these in alphabetical order
export const deleteExam = exam_id =>
    apiDelete(null, act.deleteExamSuccess, '/exam/exam/' + exam_id);
export const fetchRegistrants = () =>
    apiGet(null, act.fetchRegistrantsSuccess, '/customer/registrant');
export const getDistricts = () =>
    apiGet(act.getDistrictsRequest, act.getDistrictsSuccess, '/teacher/districts');
export const getExamPrompt = section =>
    apiGet(null, act.getExamPromptSuccess, '/exam/prompt/' + section);
export const getMetrics = item =>
    apiGet(null, act.getMetricsSuccess, '/admin/metrics?include=' + item);
export const getRegistrantOptionsByKey = key =>
    apiGet(
        act.getRegistrantOptionsByKeyRequest,
        act.getRegistrantOptionsByKeySuccess,
        '/customer/register/' + key
    );
export const getScoresForTestResponse = response_id =>
    apiGet(
        act.getScoresForTestResponseRequest,
        act.getScoresForTestResponseSuccess,
        '/admin/test-response/scores/' + response_id
    );
export const getTeachersBySchool = school_id =>
    apiGet(
        act.getTeachersBySchoolRequest,
        act.getTeachersBySchoolSuccess,
        '/teacher/teacher/' + school_id
    );
export const getTeachersStudents = () =>
    apiGet(null, act.getTeachersStudentsSuccess, '/teacher/students');
export const getTestResponsesForAdmin = () =>
    apiGet(
        act.getTestResponsesForAdminRequest,
        act.getTestResponsesForAdminSuccess,
        '/admin/test-responses'
    );
export const getTestResponsesForEditing = question_id =>
    apiGet(
        act.getTestResponsesForEditingRequest,
        act.getTestResponsesForEditingSuccess,
        '/response/fr-response-test-responses/' + question_id
    );
export const postDistrict = data =>
    apiPost(null, act.getDistrictsSuccess, '/teacher/district', data);
// Track exams currently being copied to prevent duplicates
const copyingExams = new Set();

export function postExamCopy(exam_id, original_test_id = null) {
    return async dispatch => {
        // Check if this exam is already being copied
        const copyKey = `${exam_id}_${original_test_id}`;
        if (copyingExams.has(copyKey)) {
            console.log("Exam is already being copied, skipping duplicate copy operation");
            return;
        }
        
        // Mark this exam as being copied
        copyingExams.add(copyKey);
        console.log("Starting copy operation for exam:", exam_id, "test_id:", original_test_id);
        
        dispatch(act.copyYearRequest());
        
        try {
            // Helper function to fetch all exams across all pages
            const fetchAllExams = async () => {
                const allExams = [];
                let page = 1;
                let totalPages = 1;
                
                // First, get the first page to determine total pages
                console.log(`Fetching exam list page ${page}...`);
                const firstResponse = await axios.get(`${API1}/exam/list?page=${page}&page_size=100`);
                const firstExams = firstResponse.data.exams || [];
                allExams.push(...firstExams);
                totalPages = firstResponse.data.pagination?.total_pages || 1;
                
                console.log(`Total pages to fetch: ${totalPages}, Total items: ${firstResponse.data.pagination?.total_items || 0}`);
                
                // Fetch remaining pages if there are any
                for (let currentPage = 2; currentPage <= totalPages; currentPage++) {
                    console.log(`Fetching exam list page ${currentPage}...`);
                    const response = await axios.get(`${API1}/exam/list?page=${currentPage}&page_size=100`);
                    const exams = response.data.exams || [];
                    allExams.push(...exams);
                }
                
                console.log(`Fetched ${allExams.length} total exams across ${totalPages} pages`);
                return allExams;
            };

            // Step 0: Get all exams before copy to see current state
            console.log("Fetching all exam lists before copy...");
            const beforeExams = await fetchAllExams();
            console.log("All exams before copy:", beforeExams);
            
            // Step 1: Copy exam in database (this generates new exam_id and test_id)
            const copyResponse = await axios.post(API1 + '/exam/copy', { exam_id });
            console.log("Copy exam response:", copyResponse.data);
            console.log("Full copy response object:", copyResponse);
            
            if (!copyResponse.data) {
                throw new Error("Failed to copy exam in database - no response data");
            }
            
            console.log(`Original exam ID was: ${exam_id}, original test_id was: ${original_test_id}`);
            
            // Step 1.5: Get all exams after copy to find the actual new exam
            console.log("Fetching all exam lists after copy...");
            const afterExams = await fetchAllExams();
            console.log("All exams after copy:", afterExams);
            
            // Find the actual new exam by comparing before and after lists
            
            // Find exams that exist in after but not in before (these are the new ones)
            const newExams = afterExams.filter(afterExam => 
                !beforeExams.some(beforeExam => beforeExam.exam_id === afterExam.exam_id)
            );
            
            console.log("New exams found by comparing before/after lists:", newExams);
            
            if (newExams.length === 0) {
                throw new Error("No new exam found in the list after copy operation");
            }
            
            // Use the highest ID new exam (most likely the one we just created)
            const actualNewExam = newExams.sort((a, b) => b.exam_id - a.exam_id)[0];
            const newExamId = actualNewExam.exam_id;
            const newTestId = actualNewExam.test_id;
            
            console.log("Using the actual new exam from before/after comparison:");
            console.log("New exam ID:", newExamId, "New test ID:", newTestId);
            console.log("New exam details:", actualNewExam);
            
            // Step 2: Fetch online exam data from original exam
            // Use the passed original_test_id, or try to get it from the copy response
            const originalTestId = original_test_id || copyResponse.data.original_test_id || copyResponse.data.test_id || exam_id;
            console.log(`Fetching online exam data for original exam ${exam_id}/${originalTestId}`);
            const originalExamData = await fetchOnlineExamData(exam_id, originalTestId)(dispatch);
            console.log(`Original exam data for exam ${exam_id}:`, originalExamData);
            
            if (originalExamData && originalExamData.length > 0) {
                // Step 3: Save online exam data with new exam_id/test_id
                const examDataToSave = {
                    exam_id: newExamId,
                    test_id: newTestId,
                    online_exam_data: originalExamData
                };
                
                console.log("Preparing to upload online exam data with new IDs:", {
                    newExamId,
                    newTestId,
                    originalExamDataLength: originalExamData.length
                });
                
                // Use the same S3 upload logic as saveExam
                const tokenUrl = "https://us-east-1ln7o3x5av.auth.us-east-1.amazoncognito.com/oauth2/token";
                const urlencoded = new URLSearchParams();
                urlencoded.append("grant_type", "client_credentials");
                urlencoded.append("client_id", "1nuj860i61iad27l03eevr5egu");
                urlencoded.append("client_secret", "1h17j727h26hov7enio7m08363k87r0ij85o6mm9rj36h2djqj94");
                urlencoded.append("scope", "default-m2m-resource-server-5bfkmj/read");

                const tokenResponse = await fetch(tokenUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: urlencoded,
                });
                
                const tokenData = await tokenResponse.json();
                if (!tokenData.access_token) {
                    throw new Error("Failed to retrieve Cognito token");
                }

                const token = tokenData.access_token;
                
                // Get presigned URL for new exam
                // Use the same environment detection logic as fetchOnlineExamData
                const isStaging = window.location.hostname.includes('stage') || 
                                 window.location.hostname.includes('localhost') ||
                                 process.env.NODE_ENV === 'development';
                
                const environment = isStaging ? 'stage' : 'prod';
                const apiUrl = `https://4ck22r1xs5.execute-api.us-east-1.amazonaws.com/online_testing_${environment}?env=${environment}`;
                
                console.log(`Using ${isStaging ? 'staging' : 'production'} environment for S3 upload`);
                console.log(`API URL: ${apiUrl}`);
                console.log(`isStaging value: ${isStaging} (type: ${typeof isStaging})`);
                
                const presignPayload = JSON.stringify({
                    "exam_id": newExamId.toString(),
                    "test_id": newTestId.toString(),
                    "is_staging": isStaging
                });
                
                console.log("Requesting presigned URL with payload:", presignPayload);
                console.log("Using new IDs for presigned URL request:", { newExamId, newTestId });
                
                const apiResponse = await fetch(apiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Autherization': token,
                    },
                    body: presignPayload
                });
                
                if (!apiResponse.ok) {
                    throw new Error(`Failed to get presigned URL: ${apiResponse.statusText}`);
                }
                
                const apiResponseJson = await apiResponse.json();
                const presignedUrl = apiResponseJson.upload_url;
                
                // Upload copied exam data to S3
                const examData = {
                    "online_exam_data": originalExamData,
                    "exam_id": newExamId.toString(),
                    "test_id": newTestId.toString()
                };
                
                console.log("Uploading to S3 with exam data:", examData);
                console.log("Using presigned URL:", presignedUrl);
                
                const putResponse = await fetch(presignedUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(examData)
                });
                
                if (!putResponse.ok) {
                    throw new Error(`Failed to upload copied exam data to S3: ${putResponse.statusText}`);
                }
                
                console.log(`Copied exam ${newExamId}/${newTestId} successfully uploaded to S3`);
                
                // Step 4: Verify the online exam data was saved correctly by fetching it back
                console.log("Verifying online exam data was saved correctly...");
                console.log("Note: Verification will use the same environment as the upload (production)");
                const verifyExamData = await fetchOnlineExamData(newExamId, newTestId)(dispatch);
                console.log("Verified online exam data for new exam:", verifyExamData);
            } else {
                console.log(`No online exam data found for exam ${exam_id}, skipping S3 upload`);
            }
            
            dispatch(act.fetchExamSuccess(copyResponse.data));
        } catch (error) {
            console.error("Error copying exam:", error);
            axiosErrors(error, dispatch, act.copyYearFailure);
        } finally {
            // Always remove from copying set when operation completes
            copyingExams.delete(copyKey);
            console.log("Copy operation completed for exam:", exam_id);
        }
    };
}

export function postExamCopyYear(data) {
    return async dispatch => {
        dispatch(act.copyYearRequest());
        
        try {
            // Step 1: Copy exams by year in database
            const copyResponse = await axios.post(API1 + '/exam/copy_year', data);
            if (!copyResponse.data) {
                throw new Error("Failed to copy exams by year in database");
            }
            
            console.log("Copy year response:", copyResponse.data);
            
            console.log("Exams copied by year:", copyResponse.data);
            
            // Step 2: For each copied exam, copy its online exam data
            if (copyResponse.data.copied_exams && Array.isArray(copyResponse.data.copied_exams)) {
                console.log(`Found ${copyResponse.data.copied_exams.length} exams to copy online data for`);
                
                for (const examCopy of copyResponse.data.copied_exams) {
                    const { original_exam_id, original_test_id, new_exam_id, new_test_id } = examCopy;
                    console.log(`Copying online data for exam ${original_exam_id}/${original_test_id} -> ${new_exam_id}/${new_test_id}`);
                    
                    // Fetch original online exam data
                    const originalExamData = await fetchOnlineExamData(original_exam_id, original_test_id)(dispatch);
                    
                    if (originalExamData && originalExamData.length > 0) {
                        console.log(`Found online exam data for exam ${original_exam_id}, copying to ${new_exam_id}`);
                        // Save with new IDs
                        const examDataToSave = {
                            exam_id: new_exam_id,
                            test_id: new_test_id,
                            online_exam_data: originalExamData
                        };
                        
                        // Use the same S3 upload logic
                        const tokenUrl = "https://us-east-1ln7o3x5av.auth.us-east-1.amazoncognito.com/oauth2/token";
                        const urlencoded = new URLSearchParams();
                        urlencoded.append("grant_type", "client_credentials");
                        urlencoded.append("client_id", "1nuj860i61iad27l03eevr5egu");
                        urlencoded.append("client_secret", "1h17j727h26hov7enio7m08363k87r0ij85o6mm9rj36h2djqj94");
                        urlencoded.append("scope", "default-m2m-resource-server-5bfkmj/read");

                        const tokenResponse = await fetch(tokenUrl, {
                            method: "POST",
                            headers: { "Content-Type": "application/x-www-form-urlencoded" },
                            body: urlencoded,
                        });
                        
                        const tokenData = await tokenResponse.json();
                        if (tokenData.access_token) {
                            const token = tokenData.access_token;
                            
                            // Get presigned URL for new exam
                            // Use the same environment detection logic as other functions
                            const isStaging = window.location.hostname.includes('stage') || 
                                             window.location.hostname.includes('localhost') ||
                                             process.env.NODE_ENV === 'development';
                            
                            const environment = isStaging ? 'stage' : 'prod';
                            const apiUrl = `https://4ck22r1xs5.execute-api.us-east-1.amazonaws.com/online_testing_${environment}?env=${environment}`;
                            
                            console.log(`Using ${isStaging ? 'staging' : 'production'} environment for S3 upload (copy year)`);
                            console.log(`API URL: ${apiUrl}`);
                            console.log(`isStaging value: ${isStaging} (type: ${typeof isStaging})`);
                            
                            const presignPayload = JSON.stringify({
                                "exam_id": new_exam_id.toString(),
                                "test_id": new_test_id.toString(),
                                "is_staging": isStaging
                            });
                            
                            const apiResponse = await fetch(apiUrl, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Autherization': token,
                                },
                                body: presignPayload
                            });
                            
                            if (apiResponse.ok) {
                                const apiResponseJson = await apiResponse.json();
                                const presignedUrl = apiResponseJson.upload_url;
                                
                                // Upload copied exam data to S3
                                const examData = {
                                    "online_exam_data": originalExamData,
                                    "exam_id": new_exam_id.toString(),
                                    "test_id": new_test_id.toString()
                                };
                                
                                const putResponse = await fetch(presignedUrl, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(examData)
                                });
                                
                                if (putResponse.ok) {
                                    console.log(`Copied exam ${new_exam_id}/${new_test_id} successfully uploaded to S3`);
                                } else {
                                    console.error(`Failed to upload copied exam ${new_exam_id}/${new_test_id} to S3:`, putResponse.status, putResponse.statusText);
                                }
                            }
                        }
                    } else {
                        console.log(`No online exam data found for exam ${original_exam_id}, skipping S3 upload`);
                    }
                }
            } else {
                console.log("No exams found to copy online data for, or copied_exams is not an array");
            }
            
            dispatch(act.copyYearSuccess(copyResponse.data));
        } catch (error) {
            console.error("Error copying exams by year:", error);
            axiosErrors(error, dispatch, act.copyYearFailure);
        }
    };
}
export const postExport = e => apiPost(null, act.postExportSuccess, '/admin/export/' + e);
export const postPasswordReset = user_email =>
    apiPost(null, act.resetPasswordSuccess, '/user/password-reset', user_email);
export const postRegistrants = data =>
    apiPost(act.postRegistrantsRequest, act.postRegistrantsSuccess, '/customer/registrant', data);
export const postRegistration = data =>
    apiPost(act.postRegistrationRequest, act.postRegistrationSuccess, '/customer/register', data);
export const postReport = data => apiPost(null, act.postReportSuccess, '/teacher/report', data);
export const postSchool = data => apiPost(null, act.getSchoolsSuccess, '/teacher/school', data);
export const postTeacher = data => apiPost(null, act.getTeachersSuccess, '/teacher/teacher', data);
export const putDistrict = data => apiPut(null, act.getDistrictsSuccess, '/teacher/district', data);
export const putRegistrants = data =>
    apiPut(act.postRegistrantsRequest, act.postRegistrantsSuccess, '/customer/registrant', data);
export const putSchool = data => apiPut(null, act.getSchoolsSuccess, '/teacher/school', data);
export const putTeacher = data =>
    apiPut(null, act.getTeachersSuccess, '/teacher/teacher/' + data.teacher_id, data);

// there are older (non-toast) methods of displaying error messages throughout the code
// these could be converted to "new succinct method" when it's confirmed that error handling is updated,
// as long as there are no other complexities
export function saveExam(data) {
    return async dispatch => {
        dispatch(act.saveExamRequest());
        
        try {
            let exam_id = data.exam_id;
            let test_id = data.test_id;
            
            // If this is a new exam (exam_id < 0), first create it in the database to get a proper exam_id
            if (exam_id < 0) {
                console.log("Creating new exam in database...");
                console.log("Data being sent to database:", data);
                
                // Create a copy of data without online_exam_data for database creation
                const { online_exam_data, ...dbData } = data;
                console.log("Database data (without online_exam_data):", dbData);
                
                try {
                    console.log("Creating new exam with POST to /exam/exam with data:", dbData);
                    const createResponse = await axios.post(API1 + '/exam/exam', dbData);
                    console.log("Create response:", createResponse);
                    console.log("Create response data:", createResponse.data);
                    console.log("Create response data type:", typeof createResponse.data);
                    console.log("Create response data keys:", Object.keys(createResponse.data || {}));
                    
                    if (createResponse.data) {
                        // For new exams, find the exam with matching test_id in the response
                        if (createResponse.data.exams && Array.isArray(createResponse.data.exams)) {
                            console.log("Response has exams array with length:", createResponse.data.exams.length);
                            console.log("Looking for test_id:", data.test_id, "type:", typeof data.test_id);
                            const createdExam = createResponse.data.exams.find(exam => {
                                console.log("Comparing with exam test_id:", exam.test_id, "type:", typeof exam.test_id);
                                return exam.test_id.toString() === data.test_id.toString();
                            });
                            if (createdExam) {
                                exam_id = createdExam.exam_id;
                                test_id = createdExam.test_id;
                                console.log(`New exam created successfully with exam_id: ${exam_id}, test_id: ${test_id}`);
                            } else {
                                console.log(`Could not find exam with test_id ${data.test_id} in response`);
                                console.log("Available exams in response:", createResponse.data.exams.map(e => ({ exam_id: e.exam_id, test_id: e.test_id })));
                            }
                        } else if (createResponse.data.exam_id) {
                            // Fallback to direct exam_id if response structure is different
                            exam_id = createResponse.data.exam_id;
                            test_id = createResponse.data.test_id;
                            console.log(`New exam created successfully with exam_id: ${exam_id}, test_id: ${test_id}`);
                        } else {
                            console.log("Response data structure not recognized:", createResponse.data);
                        }
                    } else {
                        console.error("Database creation failed. Response:", createResponse);
                        throw new Error(`Failed to create new exam in database. Status: ${createResponse.status}, Data: ${JSON.stringify(createResponse.data)}`);
                    }
                } catch (dbError) {
                    console.error("Database creation error:", dbError);
                    console.error("Error response:", dbError.response);
                    console.error("Error message:", dbError.message);
                    throw new Error(`Database creation failed: ${dbError.message}. Response: ${dbError.response ? JSON.stringify(dbError.response.data) : 'No response data'}`);
                }
            } else {
                // Update existing exam in database
                console.log("Updating existing exam in database...");
                console.log("Data being sent to database:", data);
                
                // Create a copy of data without online_exam_data for database update
                const { online_exam_data, ...dbData } = data;
                console.log("Database data (without online_exam_data):", dbData);
                
                try {
                    const updateResponse = await axios.post(API1 + '/exam/exam', dbData);
                    console.log("Update response:", updateResponse);
                    
                    if (!updateResponse.data) {
                        throw new Error(`Failed to update exam in database. Response: ${JSON.stringify(updateResponse.data)}`);
                    }
                    
                    console.log(`Exam ${exam_id} successfully updated in database`);
                } catch (dbError) {
                    console.error("Database update error:", dbError);
                    console.error("Error response:", dbError.response);
                    console.error("Error message:", dbError.message);
                    throw new Error(`Database update failed: ${dbError.message}. Response: ${dbError.response ? JSON.stringify(dbError.response.data) : 'No response data'}`);
                }
            }
            
            // Always upload to S3 for new exams (to create bucket structure), or if there's online exam data
            const isNewExam = data.exam_id < 0; // Check original data, not updated exam_id
            const shouldUploadToS3 = isNewExam || (data.online_exam_data && data.online_exam_data.length > 0);
            
            // For new exams, ensure we have a valid exam_id before proceeding with S3
            if (isNewExam && exam_id < 0) {
                console.error("Failed to get valid exam_id from database response, cannot proceed with S3 upload");
                throw new Error("Failed to get valid exam_id from database response");
            }
            
            if (shouldUploadToS3) {
                if (data.online_exam_data && data.online_exam_data.length > 0) {
                    console.log("Uploading online exam data to S3...");
                } else {
                    console.log("Creating S3 bucket structure for new exam (no online exam data yet)...");
                }
                
                // Step 1: Get Cognito token for API Gateway
                const tokenUrl = "https://us-east-1ln7o3x5av.auth.us-east-1.amazoncognito.com/oauth2/token";
                const urlencoded = new URLSearchParams();
                urlencoded.append("grant_type", "client_credentials");
                urlencoded.append("client_id", "1nuj860i61iad27l03eevr5egu");
                urlencoded.append("client_secret", "1h17j727h26hov7enio7m08363k87r0ij85o6mm9rj36h2djqj94");
                urlencoded.append("scope", "default-m2m-resource-server-5bfkmj/read");

                const tokenRequestOptions = {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: urlencoded,
                };

                const tokenResponse = await fetch(tokenUrl, tokenRequestOptions);
                const tokenData = await tokenResponse.json();
                
                if (!tokenData.access_token) {
                    throw new Error("Failed to retrieve Cognito token");
                }

                const token = tokenData.access_token;
                console.log("Retrieved Cognito token for saving exam");
                
                // Step 2: Get presigned URL from API Gateway for uploading to S3
                // Determine if we're in staging or production based on the current environment
                const isStaging = window.location.hostname.includes('stage') || 
                                 window.location.hostname.includes('localhost') ||
                                 process.env.NODE_ENV === 'development';
                
                const environment = isStaging ? 'stage' : 'prod';
                const apiUrl = `https://4ck22r1xs5.execute-api.us-east-1.amazonaws.com/online_testing_${environment}?env=${environment}`;
                
                console.log(`Using ${isStaging ? 'staging' : 'production'} environment for S3 upload`);
                console.log(`API URL: ${apiUrl}`);
                console.log(`isStaging value: ${isStaging} (type: ${typeof isStaging})`);
                
                const presignPayload = JSON.stringify({
                    "exam_id": exam_id.toString(),
                    "test_id": test_id.toString(),
                    "is_staging": isStaging
                });
                
                console.log("Requesting presigned URL with updated exam_id:", exam_id, "test_id:", test_id);
                console.log("Original exam_id was:", data.exam_id, "isNewExam:", isNewExam);
                
                const apiHeaders = {
                    'Content-Type': 'application/json',
                    'Autherization': token, // Use the header key the API expects
                };
                
                const apiResponse = await fetch(apiUrl, {
                    method: 'POST',
                    headers: apiHeaders,
                    body: presignPayload
                });
                
                if (!apiResponse.ok) {
                    throw new Error(`Failed to get presigned URL: ${apiResponse.statusText}`);
                }
                
                const apiResponseJson = await apiResponse.json();
                if (!apiResponseJson.upload_url) {
                    throw new Error("Upload URL not found in API response");
                }
                
                const presignedUrl = apiResponseJson.upload_url;
                
                // Step 3: Upload exam data to S3 using the presigned URL
                const examData = {
                    "online_exam_data": data.online_exam_data || [],
                    "exam_id": exam_id.toString(),
                    "test_id": test_id.toString()
                };
                
                console.log("Uploading to S3 with exam data:", examData);
                console.log("Using updated exam_id:", exam_id, "test_id:", test_id);
                
                const putHeaders = {
                    'Content-Type': 'application/json'
                };
                
                const putResponse = await fetch(presignedUrl, {
                    method: 'PUT',
                    headers: putHeaders,
                    body: JSON.stringify(examData)
                });
                
                if (!putResponse.ok) {
                    throw new Error(`Failed to upload exam data to S3: ${putResponse.statusText}`);
                }
                
                if (data.online_exam_data && data.online_exam_data.length > 0) {
                    console.log(`Exam ${exam_id}/${test_id} successfully uploaded to S3 with online exam data`);
                } else {
                    console.log(`Exam ${exam_id}/${test_id} S3 bucket structure created successfully`);
                }
            } else {
                console.log("No S3 upload needed - existing exam with no online exam data");
            }
            
            // Return the updated data with the new exam_id if it was created
            const resultData = {
                ...data,
                exam_id: exam_id,
                test_id: test_id
            };
            
            dispatch(act.saveExamSuccess(resultData));
        } catch (error) {
            console.error("Error saving exam:", error);
            axiosErrors(error, dispatch, act.saveExamFailure);
        }
    };
}

export function fetchExams(year, page = 1, pageSize = 100) {
    return async dispatch => {
        dispatch(act.fetchExamRequest());
        
        try {
            // Get exam metadata from the database only
        let url = '';
        if (year) {
            url = `${API1}/exam/list?schoolYear=${year}&page=${page}&page_size=${pageSize}`;
        } else {
            url = `${API1}/exam/list?page=${page}&page_size=${pageSize}`;
        }
            
            const response = await axios.get(url);
            dispatch(act.fetchExamSuccess(response.data));
        } catch (error) {
            console.error("Error fetching exams:", error);
            axiosErrors(error, dispatch, act.fetchExamFailure);
        }
    };
}

export function fetchReaderExams(questionId) {
    return dispatch => {
        dispatch(act.fetchReaderExamRequest());
        return axios
            .get(questionId ? `${API1}/reader/question/${questionId}` : API1 + '/reader')
            .then(resp => {
                dispatch(act.fetchReaderExamSuccess(resp.data));
            })
            .catch(err => {
                axiosErrors(err, dispatch, act.fetchReaderExamFailure);
            });
    };
}
//David's custom api (DOES NOT WORK)
export function fetchReaderExamsTeacherStudent(questionId) {
    return dispatch => {
        dispatch(act.fetchReaderExamRequest());
        let myresponseid;

        function getReaderId(aQuestionId) {
            let myresult = axios
                .get(aQuestionId ? `${API1}/reader/question/${aQuestionId}` : API1 + '/reader')
                .then(resp => {
                    dispatch(act.fetchReaderExamSuccess(resp.data));
                    console.log('response data from exams api request:');
                    console.dir(resp.data);
                    myresponseid = resp.data.response.responseId;
                    return myresponseid;
                })
                .catch(err => {
                    axiosErrors(err, dispatch, act.fetchReaderExamFailure);
                });

            console.log('axios - ');
            console.dir(myresult);
        }

        function getFreeResponseInfo(responseID) {
            let myresult = axios
                .get(`${API1}/reader/response/${responseID}`)
                .then(testresp => {
                    console.log('response data from response?');
                    console.dir(testresp.data);
                    dispatch(act.fetchReaderExamSuccess(testresp.data));
                })
                .catch(err => {
                    axiosErrors(err, dispatch, act.fetchReaderExamFailure);
                });

            console.log('axios fr - ');
            console.dir(myresult);
        }

        let responsedata = getFreeResponseInfo(getReaderId(questionId));

        let mydata = {
            myresponseid: myresponseid,
            responsedata: responsedata,
        };
        return mydata;
    };
}

export function fetchReaderResponseById(responseId) {
    return dispatch => {
        dispatch(act.fetchReaderExamRequest());
        return axios
            .get(`${API1}/reader/response/${responseId}`)
            .then(resp => {
                dispatch(act.fetchReaderExamSuccess(resp.data));
            })
            .catch(err => {
                axiosErrors(err, dispatch, act.fetchReaderExamFailure);
            });
    };
}

export function postResponseScore(questionId, data) {
    return dispatch => {
        dispatch(act.fetchReaderExamRequest());
        return axios
            .post(`${API1}/reader/${questionId}`, data)
            .then(resp => {
                dispatch(act.fetchReaderExamSuccess(resp.data));
                // resolve truthy so the caller knows the score actually saved
                return true;
            })
            .catch(err => {
                axiosErrors(err, dispatch, act.fetchReaderExamFailure);
                return false;
            });
    };
}

export function putRecategorize(questionId, responseId, data) {
    return dispatch => {
        dispatch(act.fetchReaderExamRequest());
        return axios
            .put(`${API1}/reader/response/${responseId}`, data)
            .then(resp => {
                dispatch(act.fetchReaderExamSuccess(resp.data));
            })
            .catch(err => {
                axiosErrors(err, dispatch, act.fetchReaderExamFailure);
            });
    };
}

// Save per-student extended-time accommodations, then refresh the teacher dashboard.
// `updates` = [{student_id, extra_mc_minutes, extra_fr_minutes}, ...]
export function putStudentExtraTime(updates) {
    return dispatch =>
        axios
            .put(`${API1}/teacher/students/extra-time`, {updates})
            .then(() => dispatch(getTeachersStudents()));
}

// Reopen a student's locked section (MC and/or FR) without erasing answers.
// `payload` = {student_id, reopen_mc?, reopen_fr?}
export function putStudentReopen(payload) {
    return dispatch =>
        axios
            .put(`${API1}/teacher/students/reopen`, payload)
            .then(() => dispatch(getTeachersStudents()));
}

export function refreshReaderLock(questionId, responseId) {
    return dispatch => {
        dispatch(act.refreshCheckoutRequest());
        return axios
            .patch(`${API1}/reader/response/${responseId}`)
            .then(() => {
                dispatch(act.refreshCheckoutSuccess());
            })
            .catch(err => {
                axiosErrors(err, dispatch, act.refreshCheckoutFailure);
            });
    };
}

export function cancelReaderLock(questionId, responseId) {
    return dispatch => {
        return axios
            .delete(`${API1}/reader/response/${responseId}`)
            .then(() => {
                dispatch(act.deleteCheckoutSuccess());
            })
            .catch(err => {
                axiosErrors(err, dispatch, act.deleteCheckoutFailure);
            });
    };
}

export function putStudents() {
    return (dispatch, getState) => {
        dispatch(act.putStudentsRequest());
        const state = getState();
        if (!state.student.persist || !state.student.persistChange) {
            return;
        }
        const data = Object.keys(state.student.persist)
            .filter(key => state.student.persistChange.includes(key))
            .reduce((obj, key) => {
                obj[key] = state.student.persist[key];
                return obj;
            }, {});
        axios
            .put(API1 + '/user/student', data, {headers: {'content-type': 'application/json'}})
            .then(() => {
                dispatch(act.putStudentsSuccess());
            })
            .catch(err => {
                axiosErrors(err, dispatch, act.putStudentsFailure);
            });
    };
}

export function postScannedResponse(file, history) {
    return dispatch => {
        const data = new FormData();
        data.append('file', file);
        data.append('fileHash', file.fileHash);

        axios
            .post(API1 + '/response/upload-scan', data, {
                onUploadProgress: ProgressEvent => {
                    dispatch(
                        act.postScannedResponseLoading(
                            (ProgressEvent.loaded / ProgressEvent.total) * 100
                        )
                    );
                },
            })
            .then(resp => {
                dispatch(act.postScannedResponseSuccess(resp));
                history.push('/answer/import_step_2/' + resp.data.job_id);
            })
            .catch(err => {
                axiosErrors(err, dispatch, msg => act.postScannedResponseFailure());
            });
    };
}

export function submitMCResponses(data) {
    return dispatch => {
        dispatch(act.putResponseRequest());
        return axios
            .post(API1 + '/response/mc-response', data)
            .then(() => dispatch(act.putResponseSuccess({examMCStatus: false})))
            .catch(err => axiosErrors(err, dispatch, act.putResponseFailure));
    };
}

export function submitFRResponses(payload) {
    return (dispatch, getState) => {
        dispatch(act.putResponseRequest());
        const state = getState();
        const responses = payload || state.student.responses;
        return axios
            .post(API1 + '/response/text', {questions: state.student.questions, responses})
            .then(() => dispatch(act.putResponseSuccess({examFRStatus: false})))
            .catch(err => axiosErrors(err, dispatch, act.putResponseFailure));
    };
}

export function saveTestResponse(question_id, text) {
    return dispatch => {
        dispatch(act.saveTestResponseRequest());
        return fetch(API1 + '/response/test-response/' + question_id, {
            method: 'POST',
            credentials: 'include',
            body: text,
        })
            .then(handleErrors)
            .then(response => {
                dispatch(act.saveTestResponseSuccess(text));
            })
            .catch(err => {
                dispatch(act.saveTestResponseFailure(err.message));
            });
    };
}

export function deleteTestResponse(response_id) {
    return dispatch => {
        dispatch(act.deleteTestResponseRequest(response_id));
        return fetch(API1 + '/response/fr-response-test-response/' + response_id, {
            method: 'DELETE',
            credentials: 'include',
            headers: {'content-type': 'application/json'},
        })
            .then(handleErrors)
            .then(() => {
                dispatch(act.deleteTestResponseSuccess(response_id));
            })
            .catch(err => {});
    };
}

export function fetchOnlineExamData(exam_id, test_id) {
    return async dispatch => {
        try {
            // Step 1: Get Cognito token (similar to auth.js grabToken function)
            const tokenUrl = "https://us-east-1ln7o3x5av.auth.us-east-1.amazoncognito.com/oauth2/token";
            const urlencoded = new URLSearchParams();
            urlencoded.append("grant_type", "client_credentials");
            urlencoded.append("client_id", "1nuj860i61iad27l03eevr5egu");
            urlencoded.append("client_secret", "1h17j727h26hov7enio7m08363k87r0ij85o6mm9rj36h2djqj94");
            urlencoded.append("scope", "default-m2m-resource-server-5bfkmj/read");

            const tokenRequestOptions = {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: urlencoded,
            };

            const tokenResponse = await fetch(tokenUrl, tokenRequestOptions);
            const tokenData = await tokenResponse.json();
            
            if (!tokenData.access_token) {
                throw new Error("Failed to retrieve Cognito token");
            }

            const token = tokenData.access_token;
            console.log("Retrieved Cognito token for online exam data");

            // Step 2: Get presigned URL from API Gateway
            // Determine if we're in staging or production based on the current environment
            const isStaging = window.location.hostname.includes('stage') || 
                             window.location.hostname.includes('localhost') ||
                             process.env.NODE_ENV === 'development';
            
            const environment = isStaging ? 'stage' : 'prod';
            const apiUrl = `https://4ck22r1xs5.execute-api.us-east-1.amazonaws.com/online_testing_${environment}/getexam?exam_id=${exam_id}&test_id=${test_id}&env=${environment}`;
            
            console.log(`Using ${isStaging ? 'staging' : 'production'} environment for S3 fetch`);
            
            const requestOptions = {
                method: "GET",
                headers: {
                    'Content-Type': 'application/json',
                    'Autherization': token, // Use the header key the API expects
                },
            };
            
            const apiResponse = await fetch(apiUrl, requestOptions);
            console.log('API Response status:', apiResponse.status);
            console.log('API Response ok:', apiResponse.ok);
            
            if (!apiResponse.ok) {
                const errorText = await apiResponse.text();
                console.error('API Error response:', errorText);
                throw new Error(`Failed to fetch presigned URL: ${apiResponse.statusText}`);
            }
            
            const apiResponseJson = await apiResponse.json();
            console.log('API Response JSON:', apiResponseJson);
            const presignedUrl = apiResponseJson.link;
            
            if (!presignedUrl) {
                throw new Error("Presigned URL not found in API response");
            }
            
            // Step 3: Fetch online exam data from S3
            const examResponse = await fetch(presignedUrl);
            if (!examResponse.ok) {
                if (examResponse.status === 404) {
                    console.log(`Exam data not found for exam ${exam_id}, creating new empty structure`);
                    // Return empty structure for new exams
                    return [];
                } else {
                    throw new Error(`Failed to fetch exam data from S3: ${examResponse.statusText}`);
                }
            }
            
            const examData = await examResponse.json();
            console.log(`Fetched online exam data for exam ${exam_id}:`, examData);
            
            // Return only the array, not the whole object
            if (Array.isArray(examData.online_exam_data)) {
                return examData.online_exam_data;
            } else if (
                examData.online_exam_data &&
                Array.isArray(examData.online_exam_data.sections)
            ) {
                return examData.online_exam_data.sections;
            }
            return [];
        } catch (error) {
            console.error("Error fetching online exam data:", error);
            // Return empty array if fetch fails
            return [];
        }
    };
}
